import React, { useEffect, useState } from 'react';
import { withInject } from '@piwikpro/platform';
import { InlineError } from '@piwikpro/platform/components/Errors';
import {
  Card,
  Information,
  Skeleton,
  Stack,
  Icon,
  Tag,
  Button,
  Text,
  ModalView,
  CodeSnippet,
  Trans,
  useTranslation,
  formatDate,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';
import { Notifications } from '@piwikpro/notification-crate';
import { ConflictError, NotFoundError } from '@piwikpro/http-crate/httpErrors';
import { CookieInformationService } from '../../CookieInformationService';

type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'success', data: T }
  | { status: 'error', type: string };

type TokenMeta = { createdAt: string };
type TokenValue = { value: string };

interface InjectedProps {
  notifications: Notifications
  cookieInformationService: CookieInformationService
}

const CookieInformationIntegrationTokenModal = ({
  onClose, token, notifications, t,
}: { onClose: () => void, token: string, notifications: Notifications, t: TFunction }) => (
  <ModalView title={t('cookieInformation.integrationToken.modalTitle')} onClose={onClose}>
    <Stack vertical>
      <Stack.Item>
        <Information type="warning">
          {t('cookieInformation.integrationToken.modalWarning')}
        </Information>
      </Stack.Item>
      <Stack.Item>
        <Text as="p" weight={600} dataId="Label">{t('cookieInformation.integrationToken.modalLabel')}</Text>
      </Stack.Item>
      <Stack.Item previousSpacing="extraNarrow" fill>
        <CodeSnippet
          onCopy={() => {
            notifications.push({
              text: t('cookieInformation.integrationToken.copiedNotification'),
              type: 'success',
            });
          }}
          code={token}
        />
      </Stack.Item>
    </Stack>
  </ModalView>
);

function CookieInformationIntegrationToken({
  cookieInformationService,
  notifications,
}: InjectedProps) {
  const { t } = useTranslation('administration');
  const [tokenMeta, setTokenMeta] = useState<AsyncState<TokenMeta>>({ status: 'loading' });
  const [tokenGenerate, setTokenGenerate] = useState<AsyncState<TokenValue> | null>(null);

  const fetchIntegrationToken = async () => {
    setTokenMeta({ status: 'loading' });
    try {
      const response = await cookieInformationService.getIntegrationToken();
      setTokenMeta({ status: 'success', data: { createdAt: response.createdAt } });
    } catch (error) {
      setTokenMeta({ status: 'error', type: (error as any).name });
    }
  };

  const handleCreateToken = async () => {
    setTokenGenerate({ status: 'loading' });
    try {
      const response = await cookieInformationService.createIntegrationToken();
      setTokenGenerate({ status: 'success', data: { value: response.credentials } });
      await fetchIntegrationToken();
    } catch (error) {
      if (error instanceof ConflictError) {
        await fetchIntegrationToken();
      }
      setTokenGenerate({ status: 'error', type: (error as any).name });
    }
  };

  const handleRemoveToken = async () => {
    try {
      await cookieInformationService.removeIntegrationToken();
      await fetchIntegrationToken();
    } catch (err) {
      if (err instanceof NotFoundError) {
        await fetchIntegrationToken();
      }
    }
  };

  useEffect(() => {
    fetchIntegrationToken();
  }, []);

  const tokenExists = tokenMeta.status === 'success';
  const isLoading = tokenMeta.status === 'loading'
    || tokenGenerate?.status === 'loading';
  const hasCriticalError = tokenMeta.status === 'error' && tokenMeta.type !== 'NotFoundError';
  const hasNoToken = tokenMeta.status === 'error' && tokenMeta.type === 'NotFoundError';

  const renderTokenStatus = () => {
    if (isLoading) {
      return <Skeleton width={142} height={24} />;
    }

    if (hasCriticalError) {
      return <InlineError />;
    }

    if (hasNoToken) {
      return (
        <Tag color="gray">
          {t('cookieInformation.integrationToken.noTokenGenerated')}
        </Tag>
      );
    }

    if (tokenMeta.status === 'success') {
      return (
        <Tag color="green">
          <Trans
            i18nKey="cookieInformation.integrationToken.generatedOn"
            values={{
              date: formatDate(new Date(tokenMeta.data.createdAt), { format: 'LL' }),
            }}
          />
        </Tag>
      );
    }

    return null;
  };

  return (
    <Card>
      <Card.Section>
        <Stack vertical horizontalAlignment="middle">
          <Stack.Item fill>
            <Stack>
              <Stack.Item fill>
                <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('cookieInformation.integrationToken.header')}</Text>
              </Stack.Item>
              <Stack.Item>
                <Icon size="medium" name="cookie-information" />
              </Stack.Item>
            </Stack>
          </Stack.Item>
          <Stack.Item>
            <Text as="p">
              {t('cookieInformation.integrationToken.description')}
            </Text>
          </Stack.Item>
          <Stack.Item fill>
            <Card>
              <Card.Section subdued padding="narrow">
                <Stack verticalAlignment="center">
                  <Stack.Item fill>
                    <Stack verticalAlignment="center" spacing="narrow">
                      <Stack.Item fill={hasCriticalError}>
                        <Text as="p" weight={600} dataId="Label">{t('cookieInformation.integrationToken.label')}</Text>
                      </Stack.Item>
                      <Stack.Item>
                        {renderTokenStatus()}
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  {!hasCriticalError && (
                    <Stack.Item previousSpacing="narrow">
                      <Button
                        appearance={tokenExists ? 'destructive-simple' : 'default'}
                        text={tokenExists ? t('cookieInformation.integrationToken.removeButton') : t('cookieInformation.integrationToken.generateButton')}
                        onClick={tokenExists ? handleRemoveToken : handleCreateToken}
                        pending={isLoading}
                        disabled={isLoading}
                      />
                    </Stack.Item>
                  )}
                </Stack>
              </Card.Section>
            </Card>
          </Stack.Item>
        </Stack>
      </Card.Section>
      {tokenGenerate?.status === 'success' && (
        <CookieInformationIntegrationTokenModal
          onClose={() => setTokenGenerate(null)}
          token={tokenGenerate.data.value}
          notifications={notifications}
          t={t}
        />
      )}
    </Card>
  );
}

export default withInject<InjectedProps>({
  cookieInformationService: 'CookieInformationCrate.CookieInformationService',
  notifications: 'NotificationCrate.notifications',
})(CookieInformationIntegrationToken);
